import type {
  PaymentRemittanceCreateResult,
  PaymentRemittanceCreatedUpload,
  PaymentRemittanceFinalizeResult,
} from "@/lib/api/paymentRemittancePdfs";
import type { PossibleReturnType } from "@/lib/responseResults";

import type { FileEntry } from "../state/types";
import type {
  UploadGroupInput,
  UploadGroupResult,
  UploadPresignedFileResult,
  UploadStatus,
  UploaderDeps,
} from "./types";

/**
 * 3 total attempts per retryable I/O call (initial + 2 retries).
 * Backoff is linear: 500ms before retry #1, 1000ms before retry #2.
 */
const MAX_ATTEMPTS = 3;
const BACKOFF_INCREMENT_MS = 500;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Run an async I/O call with bounded linear-backoff retries.
 *
 * The predicate `isRetryable` inspects the result and decides whether
 * to spend another attempt. Stops as soon as `isRetryable` returns
 * `false` or `MAX_ATTEMPTS` is reached. Returns the final result
 * regardless of success.
 */
async function withRetries<T>(
  call: () => Promise<T>,
  isRetryable: (result: T) => boolean,
): Promise<T> {
  let attempt = 0;
  // Loop runs at most MAX_ATTEMPTS times.
  while (true) {
    attempt += 1;
    const result = await call();
    if (attempt >= MAX_ATTEMPTS) return result;
    if (!isRetryable(result)) return result;
    // Linear backoff: 500ms, then 1000ms.
    await sleep(BACKOFF_INCREMENT_MS * attempt);
  }
}

function isApiCallRetryable<T>(result: PossibleReturnType<T>): boolean {
  // Retry on transient backend errors (HTTP 500) and throttling
  // (HTTP 429). Validation (400) and auth (401) are not retried.
  if (result.success) return false;
  return result.statusCode === 500 || result.statusCode === 429;
}

function isPresignedPutRetryable(result: UploadPresignedFileResult): boolean {
  if (result.success) return false;
  const status = result.data.status;
  // `status === undefined` means fetch threw before reaching the
  // server (network error). Treat as transient.
  if (status === undefined) return true;
  if (status === 429) return true;
  return status >= 500 && status < 600;
}

/**
 * Backend sorts files in `createBatch` by:
 *   1. filename.trim().toLowerCase() ascending
 *   2. original input order as stable tie-break
 *
 * The frontend must construct the exact same ordering BEFORE calling
 * create, so it can deterministically map returned upload rows by
 * `upload_order_index` instead of by filename (which is not unique).
 */
function normalizeFilenameForBackendSort(filename: string): string {
  return filename.trim().toLowerCase();
}

function orderFilesForCreate(fileEntries: FileEntry[]): FileEntry[] {
  return [...fileEntries]
    .map((entry: FileEntry, originalInputIndex: number) => ({
      entry,
      originalInputIndex,
      sortFilename: normalizeFilenameForBackendSort(entry.file.name),
    }))
    .sort(
      (
        left: {
          entry: FileEntry;
          originalInputIndex: number;
          sortFilename: string;
        },
        right: {
          entry: FileEntry;
          originalInputIndex: number;
          sortFilename: string;
        },
      ) => {
        if (left.sortFilename < right.sortFilename) return -1;
        if (left.sortFilename > right.sortFilename) return 1;
        return left.originalInputIndex - right.originalInputIndex;
      },
    )
    .map(
      (item: {
        entry: FileEntry;
        originalInputIndex: number;
        sortFilename: string;
      }) => item.entry,
    );
}

function contentTypeFor(file: File): string {
  return file.type || "application/pdf";
}

/**
 * Pure async function that runs the existing 3-step backend flow for
 * one customer group:
 *
 *   1. POST payment-remittance/batches
 *   2. PUT each PDF to its returned presigned URL (sequentially, so the
 *      `done` counter stays monotonic)
 *   3. POST payment-remittance/batches/:id/finalize
 *
 * I/O is dependency-injected via `deps`; this function does not call
 * React hooks. Status transitions are surfaced through `onStatus` so
 * the wizard's progress strip can re-render on each step.
 *
 * Retry semantics: this function never retries internally. If it
 * fails, a fresh batch must be created by calling it again — matching
 * v1 behavior of the existing `CreateBatchDialog`.
 *
 * If any presigned upload fails, finalize is intentionally skipped to
 * avoid finalising a partial batch.
 */
export async function uploadOneGroup(
  input: UploadGroupInput,
  deps: UploaderDeps,
  onStatus: (status: UploadStatus) => void,
): Promise<UploadGroupResult> {
  const { group, files, period_date } = input;

  if (group.selected_customer_id === null) {
    const message = "Group has no selected customer";
    onStatus({ kind: "failed", message });
    return { kind: "failed", message };
  }
  if (group.file_ids.length === 0) {
    const message = "Group has no files";
    onStatus({ kind: "failed", message });
    return { kind: "failed", message };
  }

  // Resolve and order the file entries.
  const orderedFileEntries: FileEntry[] = (() => {
    const out: FileEntry[] = [];
    for (const fileId of group.file_ids) {
      const entry = files[fileId];
      if (!entry) continue;
      out.push(entry);
    }
    return orderFilesForCreate(out);
  })();

  if (orderedFileEntries.length !== group.file_ids.length) {
    const message =
      "Internal error: some files referenced by the group could not be resolved";
    onStatus({ kind: "failed", message });
    return { kind: "failed", message };
  }

  // ---- Step 1: create ---------------------------------------------------
  // Safe to retry: until createBatch succeeds, no backend batch exists.
  // Once it does succeed, we never call it again for this group.
  onStatus({ kind: "creating" });
  const createResult: PossibleReturnType<PaymentRemittanceCreateResult> =
    await withRetries(
      () =>
        deps.createBatch({
          customer_id: group.selected_customer_id as string,
          period_date,
          files: orderedFileEntries.map((entry: FileEntry) => ({
            filename: entry.file.name,
            content_type: contentTypeFor(entry.file),
            size_bytes: entry.file.size,
          })),
        }),
      isApiCallRetryable,
    );

  if (!createResult.success || !createResult.data) {
    const message = `Create failed after ${MAX_ATTEMPTS} attempts: ${
      createResult.message || "unknown error"
    }`;
    onStatus({ kind: "failed", message });
    return { kind: "failed", message };
  }

  const { batch_id, uploads } = createResult.data;

  if (uploads.length !== orderedFileEntries.length) {
    const message = `Create failed: backend returned ${uploads.length} upload row(s) for ${orderedFileEntries.length} file(s)`;
    onStatus({ kind: "failed", message });
    return { kind: "failed", message };
  }

  // ---- Step 2: upload PDFs ---------------------------------------------
  // Files are uploaded sequentially so the `done` counter is monotonic
  // and the operator's progress display is meaningful (per spec).
  onStatus({ kind: "uploading", total: uploads.length, done: 0 });

  for (let i = 0; i < uploads.length; i += 1) {
    const upload: PaymentRemittanceCreatedUpload = uploads[i];
    const index = upload.upload_order_index;
    if (
      !Number.isInteger(index) ||
      index < 0 ||
      index >= orderedFileEntries.length
    ) {
      const message = `Upload failed: backend returned out-of-range upload_order_index ${index} for ${upload.original_filename}`;
      onStatus({ kind: "failed", message });
      return { kind: "failed", message };
    }
    const entry = orderedFileEntries[index];
    // S3 PUTs to a presigned URL are idempotent, so retrying the same
    // PUT is safe even after one already partially succeeded.
    const putResult = await withRetries(
      () =>
        deps.uploadPresignedFile(
          upload.presigned_put_url,
          entry.file,
          contentTypeFor(entry.file),
        ),
      isPresignedPutRetryable,
    );
    if (!putResult.success) {
      const httpStatus = putResult.data.status;
      const message = `Upload failed for ${entry.file.name} after ${MAX_ATTEMPTS} attempts: S3 PUT status ${
        httpStatus ?? "network error"
      }`;
      onStatus({ kind: "failed", message });
      return { kind: "failed", message };
    }
    onStatus({ kind: "uploading", total: uploads.length, done: i + 1 });
  }

  // ---- Step 3: finalize ------------------------------------------------
  onStatus({ kind: "finalizing" });
  const finalizeResult: PossibleReturnType<PaymentRemittanceFinalizeResult> =
    await withRetries(() => deps.finalizeBatch(batch_id), isApiCallRetryable);

  if (!finalizeResult.success || !finalizeResult.data) {
    const message = `Finalize failed after ${MAX_ATTEMPTS} attempts: ${
      finalizeResult.message || "unknown error"
    }`;
    onStatus({ kind: "failed", message });
    return { kind: "failed", message };
  }

  const terminal: UploadGroupResult = {
    kind: "complete",
    batch_id,
    process_job_id: finalizeResult.data.process_job_id,
  };
  onStatus(terminal);
  return terminal;
}
